/**
 * SupplierBolEditPage.jsx
 * ───────────────────────
 * Screen 9 — BOL Edit / Reopen.
 * Route: /supplier/disposal/:projectId/bol/:bolId/edit
 *
 * Phase 0: Confirm reopen (online-only API call)
 * Phase 1: Remove tools, then generate amended BOL for signature
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../../shared/context/UserContext';
import { reopenBol, getBolToolDetails } from '../../api/supplierDisposalApi';
import { useDisposalOfflineQueue } from '../../hooks/useDisposalOfflineQueue';
import SupplierLayout from '../../layouts/SupplierLayout';
import './SupplierBolEditPage.css';

function getAmendedBolNumber(bolNumber) {
  const match = bolNumber.match(/^(.+)-A(\d*)$/);
  if (match) {
    const base = match[1];
    const current = match[2] ? parseInt(match[2], 10) : 1;
    return `${base}-A${current + 1}`;
  }
  return `${bolNumber}-A`;
}

export default function SupplierBolEditPage() {
  const { projectId, bolId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { callerEmail } = useUser();
  const { queueRemoveTool, drainDisposalQueue } = useDisposalOfflineQueue(callerEmail);

  const { bol, project } = location.state || {};

  // Tool detail enrichment — fetch by specific tool IDs (works for any disposal_status)
  const [toolPool, setToolPool] = useState([]);
  useEffect(() => {
    if (!callerEmail || !projectId || !bol?.tools) return;
    const toolIds = bol.tools.filter(t => !t.removed_at).map(t => t.tool_id);
    if (toolIds.length === 0) return;
    getBolToolDetails(callerEmail, projectId, toolIds)
      .then(data => {
        const list = Array.isArray(data) ? data : data.results || [];
        setToolPool(list);
      })
      .catch(() => { /* non-blocking */ });
  }, [callerEmail, projectId, bol]);

  // Phase
  const [phase, setPhase] = useState(0);
  const [reopenLoading, setReopenLoading] = useState(false);
  const [reopenError, setReopenError] = useState(null);
  const [generateLoading, setGenerateLoading] = useState(false);

  // Remove flow
  const [removedToolIds, setRemovedToolIds] = useState(new Set());
  const [confirmDialogTool, setConfirmDialogTool] = useState(null);
  const [removeReason, setRemoveReason] = useState('');
  const [abandonConfirm, setAbandonConfirm] = useState(false);

  // Active tools from the BOL (not already removed before this session)
  const activeTools = useMemo(() => {
    if (!bol?.tools) return [];
    return bol.tools.filter(t => !t.removed_at);
  }, [bol]);

  // Enrich active tools with pool data
  const enrichedTools = useMemo(() => {
    return activeTools.map(entry => {
      const detail = toolPool.find(t =>
        (t.id || t.tool_id) === entry.tool_id
      );
      return {
        ...entry,
        supplier_tool_id: detail?.supplier_tool_id || entry.tool_id,
        estimated_weight: detail?.estimated_weight || detail?.weight_lbs || 0,
        site_location: detail?.site_location || '',
        detail_location: detail?.detail_location || '',
      };
    });
  }, [activeTools, toolPool]);

  const remainingTools = useMemo(() =>
    enrichedTools.filter(t => !removedToolIds.has(t.tool_id)),
    [enrichedTools, removedToolIds]
  );

  const removedTools = useMemo(() =>
    enrichedTools.filter(t => removedToolIds.has(t.tool_id)),
    [enrichedTools, removedToolIds]
  );

  const totalWeight = useMemo(() =>
    activeTools.reduce((sum, t) => {
      const detail = toolPool.find(x => (x.id || x.tool_id) === t.tool_id);
      return sum + (detail?.estimated_weight || detail?.weight_lbs || 0);
    }, 0),
    [activeTools, toolPool]
  );

  const remainingWeight = useMemo(() =>
    remainingTools.reduce((sum, t) => sum + (t.estimated_weight || 0), 0),
    [remainingTools]
  );

  const bolNumber = bol?.bol_number || bolId?.substring(0, 12) || '—';

  // ── Phase 0: Reopen ────────────────────────────────────────────────
  const handleReopen = useCallback(async () => {
    if (!navigator.onLine) {
      setReopenError('Reopening requires an internet connection.');
      return;
    }
    setReopenLoading(true);
    setReopenError(null);
    try {
      await reopenBol(callerEmail, bolId, projectId);
      setPhase(1);
    } catch (err) {
      setReopenError(err.message || 'Reopen failed');
    } finally {
      setReopenLoading(false);
    }
  }, [callerEmail, bolId, projectId]);

  // ── Phase 1: Remove tool ───────────────────────────────────────────
  function handleConfirmRemove() {
    if (!confirmDialogTool) return;
    const tid = confirmDialogTool.tool_id;
    setRemovedToolIds(prev => new Set(prev).add(tid));
    queueRemoveTool({
      bol_id: bolId,
      project_id: projectId,
      tool_id: tid,
      removed_by: callerEmail,
      removed_reason: removeReason.trim() || 'Removed during reopen',
    });
    setConfirmDialogTool(null);
    setRemoveReason('');
  }

  // ── Generate amended BOL ───────────────────────────────────────────
  const handleGenerate = useCallback(async () => {
    setGenerateLoading(true);
    try {
      await drainDisposalQueue(callerEmail);

      const amendedBolNumber = getAmendedBolNumber(bol.bol_number);
      const amendedBol = {
        ...bol,
        bol_number: amendedBolNumber,
        tools: bol.tools.filter(t =>
          t.removed_at === null && !removedToolIds.has(t.tool_id)
        ),
      };
      const loadedTools = remainingTools.map(t => {
        const detail = toolPool.find(x => (x.id || x.tool_id) === t.tool_id);
        return detail || t;
      });

      navigate(`/supplier/disposal/${projectId}/bol/${bolId}/preview`, {
        state: {
          bol: amendedBol,
          project,
          loadedTools,
          isAmendedBol: true,
          sessionKey: null,
        },
      });
    } catch (err) {
      console.error('Generate amended BOL failed:', err);
      setGenerateLoading(false);
    }
  }, [bol, project, projectId, bolId, removedToolIds, remainingTools, toolPool, callerEmail, drainDisposalQueue, navigate]);

  // ── Back handler ───────────────────────────────────────────────────
  function handleBack() {
    if (phase === 1) {
      setAbandonConfirm(true);
    } else {
      navigate(`/supplier/disposal/${projectId}/log`);
    }
  }

  // Missing state
  if (!bol || !project) {
    return (
      <SupplierLayout>
        <div className="be-header">
          <button className="be-back" onClick={() => navigate(`/supplier/disposal/${projectId}/log`)}>&larr;</button>
          <span className="be-header-title">BOL Edit</span>
          <span className="be-header-logo">TMG</span>
        </div>
        <div className="be-state">Session data not found. Please go back and try again.</div>
      </SupplierLayout>
    );
  }

  // ═══════════════════════════════════════════════════════════════════
  // PHASE 0 — Confirm Reopen
  // ═══════════════════════════════════════════════════════════════════
  if (phase === 0) {
    return (
      <SupplierLayout>
        <div className="be-header">
          <button className="be-back" onClick={handleBack}>&larr;</button>
          <span className="be-header-title">BOL Edit</span>
          <span className="be-header-logo">TMG</span>
        </div>

        <div className="be-phase0">
          <div className="be-warn-icon">&#9888;</div>
          <div className="be-warn-title">Reopen this shipment?</div>

          <div className="be-warn-summary">
            <div className="be-warn-row">
              <span className="be-warn-label">BOL Number</span>
              <span className="be-warn-value be-mono">{bolNumber}</span>
            </div>
            <div className="be-warn-row">
              <span className="be-warn-label">Tools</span>
              <span className="be-warn-value">{activeTools.length}</span>
            </div>
            <div className="be-warn-row">
              <span className="be-warn-label">Est. Weight</span>
              <span className="be-warn-value">{totalWeight.toLocaleString()} lbs</span>
            </div>
          </div>

          <div className="be-warn-text">
            Reopening will allow you to remove tools from this shipment.
            A new amended BOL will be generated for the driver to sign.
          </div>

          {reopenError && (
            <div className="be-error">{reopenError}</div>
          )}

          <div className="be-phase0-actions">
            <button className="be-btn-secondary" onClick={() => navigate(`/supplier/disposal/${projectId}/log`)}>
              &larr; Cancel
            </button>
            <button className="be-btn-primary" onClick={handleReopen} disabled={reopenLoading}>
              {reopenLoading ? 'Reopening...' : 'Reopen Shipment \u2192'}
            </button>
          </div>
        </div>
      </SupplierLayout>
    );
  }

  // ═══════════════════════════════════════════════════════════════════
  // PHASE 1 — Remove Tools
  // ═══════════════════════════════════════════════════════════════════
  return (
    <SupplierLayout>
      <div className="be-header">
        <button className="be-back" onClick={handleBack}>&larr;</button>
        <span className="be-header-title">BOL Edit</span>
        <span className="be-header-logo">TMG</span>
      </div>

      {/* Reopened warning strip */}
      <div className="be-reopen-strip">
        SHIPMENT REOPENED — Remove tools then generate amended BOL
      </div>

      {/* BOL summary */}
      <div className="be-bol-summary">
        <div className="be-bol-summary-row">
          <span>{bolNumber}</span>
          <span>{bol.truck_type}</span>
          <span>{bol.hauler_company}</span>
        </div>
        <div className="be-bol-summary-count">
          Active tools remaining: {remainingTools.length}
          {remainingWeight > 0 && ` \u00B7 ${remainingWeight.toLocaleString()} lbs`}
        </div>
      </div>

      {/* Tool list */}
      <div className="be-tool-list">
        {/* Active / remaining tools */}
        {remainingTools.map(t => (
          <div key={t.tool_id} className="be-tool-row">
            <div className="be-tool-info">
              <div className="be-tool-id">{t.supplier_tool_id || t.tool_id}</div>
              <div className="be-tool-loc">
                {[t.site_location, t.detail_location].filter(Boolean).join(' \u00B7 ') || '\u2014'}
              </div>
            </div>
            <span className="be-tool-weight">
              {t.estimated_weight ? `${Number(t.estimated_weight).toLocaleString()} lbs` : '\u2014'}
            </span>
            <button className="be-remove-btn" onClick={() => {
              setConfirmDialogTool(t);
              setRemoveReason('');
            }}>
              &times;
            </button>
          </div>
        ))}

        {/* Removed tools — strikethrough */}
        {removedTools.map(t => (
          <div key={t.tool_id} className="be-tool-row be-tool-removed">
            <div className="be-tool-info">
              <div className="be-tool-id be-strike">{t.supplier_tool_id || t.tool_id}</div>
              <div className="be-tool-loc be-strike">
                {[t.site_location, t.detail_location].filter(Boolean).join(' \u00B7 ') || '\u2014'}
              </div>
            </div>
            <span className="be-tool-weight be-strike">
              {t.estimated_weight ? `${Number(t.estimated_weight).toLocaleString()} lbs` : '\u2014'}
            </span>
            <span className="be-removed-badge">REMOVED</span>
          </div>
        ))}
      </div>

      {/* Running count */}
      {removedToolIds.size > 0 && (
        <div className="be-removed-count">
          {removedToolIds.size} tool{removedToolIds.size !== 1 ? 's' : ''} removed this session
        </div>
      )}

      {/* Generate button — sticky bottom */}
      <div className="be-bottom-bar">
        <button
          className={`be-generate-btn ${removedToolIds.size === 0 ? 'disabled' : ''}`}
          onClick={handleGenerate}
          disabled={removedToolIds.size === 0 || generateLoading}
        >
          {generateLoading ? 'Generating...' : 'Generate Amended BOL \u2192'}
        </button>
      </div>

      {/* ── Remove confirmation dialog ────────────────────────────────── */}
      {confirmDialogTool && (
        <div className="be-dialog-overlay" onClick={() => setConfirmDialogTool(null)}>
          <div className="be-dialog-card" onClick={e => e.stopPropagation()}>
            <div className="be-dialog-text">
              Remove <strong>{confirmDialogTool.supplier_tool_id || confirmDialogTool.tool_id}</strong> from this shipment?
            </div>
            <input
              type="text"
              className="be-dialog-input"
              value={removeReason}
              onChange={e => setRemoveReason(e.target.value)}
              placeholder="Reason (optional)"
            />
            <div className="be-dialog-btns">
              <button className="be-dialog-cancel" onClick={() => setConfirmDialogTool(null)}>Cancel</button>
              <button className="be-dialog-confirm" onClick={handleConfirmRemove}>Remove Tool</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Abandon confirmation dialog ───────────────────────────────── */}
      {abandonConfirm && (
        <div className="be-dialog-overlay" onClick={() => setAbandonConfirm(false)}>
          <div className="be-dialog-card" onClick={e => e.stopPropagation()}>
            <div className="be-dialog-text">
              Abandon this edit? Tool removals already queued will still be processed.
            </div>
            <div className="be-dialog-btns">
              <button className="be-dialog-cancel" onClick={() => setAbandonConfirm(false)}>Stay</button>
              <button className="be-dialog-confirm" onClick={() => navigate(`/supplier/disposal/${projectId}/log`)}>
                Leave
              </button>
            </div>
          </div>
        </div>
      )}
    </SupplierLayout>
  );
}
